import React from 'react';
import { ArrowRightIcon, ArrowUpRightIcon, BadgeCheckIcon, HeartHandshakeIcon, MapPinIcon } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { SISTER_ORGANIZATIONS, SisterOrganization } from '../data/sister-organizations';
const NETWORK_LOGO = 'https://cdn.magicpatterns.com/uploads/eZ5rmta1LUT5zNjEpkMWnd/HAA_National_Network.png';
export function NationalNetworkSection() {
  const {
    language
  } = useLanguage();
  const isEs = language === 'ES';
  const count = SISTER_ORGANIZATIONS.length;
  return <section id="network" className="w-full bg-[#F8FAFF] py-16 lg:py-24" aria-labelledby="network-heading" data-id="element-2846" data-mp="3lfckfjl7wzhn">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2847" data-mp="3bizlo7atfvg1">
        {/* Header: logo leads, heading and CTA beside it */}
        <div className="grid gap-10 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:items-center mb-12 lg:mb-16" data-id="element-2848" data-mp="1w1fspy4oruv4">
          <a href="#/national-network" className="block rounded-2xl bg-white border border-haa-navy/10 p-6 sm:p-8 transition-shadow duration-200 hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" aria-label={isEs ? 'Ir a la página de la Red Nacional Hondureño-Americana' : 'Go to the Honduran-American National Network page'} data-id="element-2849" data-mp="205nmi9q04107">
            <img src={NETWORK_LOGO} alt="Honduran-American National Network" className="w-full h-auto max-h-56 object-contain" loading="lazy" decoding="async" data-id="element-2850" data-mp="100rqbogcuwrg" />
          </a>

          <div data-id="element-2851" data-mp="19tfp2oqwbast">
            <h2 id="network-heading" className="font-bebas text-6xl sm:text-7xl lg:text-8xl text-haa-navy tracking-wider leading-none" data-id="element-2852" data-mp="14s9pc6ytin9s">
              {isEs ? 'Red Nacional' : 'National Network'}
            </h2>
            <p className="font-nunito text-lg text-gray-600 mt-4 max-w-2xl leading-relaxed" data-id="element-2853" data-mp="1duj1f19qiopa">
              {isEs ? 'Organizaciones hondureño-americanas y aliadas que trabajan juntas para fortalecer comunidades en Estados Unidos y en Honduras. Conozca a quienes forman la red y apoye su labor directamente.' : 'Honduran-American and allied organizations working together to strengthen communities across the United States and in Honduras. Meet the organizations in the network and support their work directly.'}
            </p>

            <div className="flex flex-wrap items-center gap-x-8 gap-y-4 mt-8" data-id="element-2854" data-mp="z4l6ynvc4wq2">
              <a href="#/national-network" className="inline-flex items-center gap-2 rounded-full bg-haa-blue px-6 py-3 font-nunito font-bold text-white transition-colors duration-150 hover:bg-haa-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue focus-visible:ring-offset-2" data-id="element-2855" data-mp="1447jsw794ayc">
                {isEs ? 'Explorar la Red Nacional' : 'Explore the National Network'}
                <ArrowRightIcon className="w-4 h-4" aria-hidden="true" data-id="element-2856" data-mp="32dwk0q33fs4g" />
              </a>
              <p className="font-nunito text-sm text-gray-600" data-id="element-2857" data-mp="2xmekm6gpxzll">
                <span className="font-bebas text-3xl text-haa-navy tracking-wide align-middle mr-2" data-id="element-2858" data-mp="2xl3i09z0exw2">
                  {count}
                </span>
                {isEs ? 'organizaciones participantes' : 'participating organizations'}
              </p>
            </div>
          </div>
        </div>

        {/* Organizations, pulled directly from the National Network roster */}
        <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5" data-id="element-2859" data-mp="3ge7cfxk1xerq">
          {SISTER_ORGANIZATIONS.map(org => <OrganizationCard key={org.name} org={org} isEs={isEs} data-id="element-2860" data-mp="37czpgbmzk0cy" />)}
        </ul>
      </div>
    </section>;
}
type OrganizationCardProps = {
  org: SisterOrganization;
  isEs: boolean;
};
function OrganizationCard({
  org,
  isEs
}: OrganizationCardProps) {
  return <li className="flex flex-col rounded-2xl bg-white border border-haa-navy/10 overflow-hidden" data-id="element-2861" data-mp="1vzxb7n8czmvy">
      <div className="flex h-32 items-center justify-center bg-white px-5 border-b border-haa-navy/5" data-id="element-2862" data-mp="3jsfnpcz1ldqe">
        {org.logo ? <img src={org.logo} alt={`${org.name} logo`} className="max-h-24 w-full object-contain" loading="lazy" decoding="async" data-id="element-2863" data-mp="3au3zfhlnes14" /> : <span className="flex h-16 w-16 items-center justify-center rounded-xl font-bebas text-2xl text-white tracking-wide" style={{
        backgroundColor: org.accent
      }} aria-hidden="true" data-id="element-2864" data-mp="6gfbrz9xy95n">
            {initials(org.name)}
          </span>}
      </div>

      <div className="flex flex-1 flex-col p-5" data-id="element-2865" data-mp="2cpmf7lfznjk">
        <p className="flex items-center gap-1.5 font-nunito text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-700" data-id="element-2866" data-mp="2i4wrxt47us3q">
          <BadgeCheckIcon className="w-3.5 h-3.5 shrink-0" aria-hidden="true" data-id="element-2867" data-mp="2n49qhb4g9nm5" />
          {isEs ? '501(c)(3) Registrada' : 'Registered 501(c)(3)'}
        </p>
        <h3 className="font-nunito font-extrabold text-haa-navy text-base leading-snug mt-2" data-id="element-2868" data-mp="2eotxrx0svuxa">
          {org.name}
        </h3>
        <p className="font-nunito text-sm text-haa-blue font-semibold mt-1" data-id="element-2869" data-mp="2okx1v8f7q3mv">
          {isEs ? org.focusEs : org.focus}
        </p>
        <p className="flex items-center gap-1.5 font-nunito text-sm text-gray-600 mt-2" data-id="element-2870" data-mp="2jndm242v9ist">
          <MapPinIcon className="w-3.5 h-3.5 shrink-0 text-gray-500" aria-hidden="true" data-id="element-2871" data-mp="1vxj9zoqdfy9m" />
          {org.location}
        </p>

        <div className="mt-auto pt-5 flex flex-wrap gap-2" data-id="element-2872" data-mp="1qz7zdbs95jqc">
          {org.donateUrl ? <a href={org.donateUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#9FD3F5] px-3.5 py-1.5 font-nunito text-xs font-bold text-haa-navy transition-colors duration-150 hover:bg-[#7cc2ef] focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" aria-label={`${isEs ? 'Donar a' : 'Donate to'} ${org.name}`} data-id="element-2873" data-mp="25nvpv3n8r39f">
              <HeartHandshakeIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2874" data-mp="3d7g73vp11h46" />
              {isEs ? 'Donar' : 'Donate'}
            </a> : null}
          {org.url ? <a href={org.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-haa-navy px-3.5 py-1.5 font-nunito text-xs font-bold text-white transition-colors duration-150 hover:bg-haa-blue focus:outline-none focus-visible:ring-2 focus-visible:ring-haa-blue" aria-label={`${isEs ? 'Visitar el sitio de' : 'Visit website for'} ${org.name}`} data-id="element-2875" data-mp="3m0bfgy9sjsy6">
              {isEs ? 'Sitio web' : 'Website'}
              <ArrowUpRightIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-2876" data-mp="djq45atosnrw" />
            </a> : null}
        </div>
      </div>
    </li>;
}
function initials(name: string) {
  return name.split(/\s+/).filter(w => /^[A-ZÁÉÍÓÚÑ]/.test(w)).slice(0, 2).map(w => w[0]).join('');
}